import { PlusOutlined, FileExcelOutlined } from "@ant-design/icons";
import { Button, Divider } from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppContext from "../../context/app/context";
import TripContext from "../../context/trip/context";
import { useIsMount } from "../../utils/hooks/is-mount";
import styles from "./style.module.scss";
import TripContextProvider from "../../context/trip/provider";
import TripTable from "../../components/trip/trip-table";
import TripFilter from "../../components/trip/trip-filter";
import SearchFilterCard from "../../components/general/filter-card";
import Can from "../../utils/rbac/can";
import PageHeader from "../../components/general/layout/page-header";
import RefreshBtn from "../../components/general/table-components/actions/refresh-btn";
import { Permissions } from "../../utils/Permissions/Permissions";
import ExportToExcelModal from "../../components/general/excel/export-to-excel-modal";
import {
  IExportTripExcel,
  columnsExportTripExcel,
} from "../../models/trip/request";
import TemplateExportExcel from "../../components/general/excel/expor-excel";

interface IProps {
  children?: React.ReactNode;
}

const TripComponent: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const navigate = useNavigate();

  const [search, setSearch] = useState<string | undefined>(undefined);
  const [exportModalVisible, setExportModalVisible] = useState(false);

  const { actions, loading, query } = useContext(TripContext);

  useEffect(() => {
    actions.setQuery({ ...query, keyword: search });
  }, [search]);

  const isMounted = useIsMount();
  useEffect(() => {
    !isMounted && actions.getList();
  }, [query, direction]);

  return (
    <div dir={direction} className={styles.container}>
      <PageHeader
        title={t("trips")}
        subTitle={t("trips_management")}
        extra={[
          <RefreshBtn
            key={0}
            loading={loading.includes("list")}
            onClick={() => actions.getList()}
          >
            {t("refresh")}
          </RefreshBtn>,
          <Can key={5} perform={Permissions.CAN_EXPORT_TRIP}>
            <Divider key={1} type="vertical" />
            <Button
              key={2}
              type="primary"
              onClick={() => {
                setExportModalVisible(true);
              }}
              icon={<FileExcelOutlined />}
            >
              {t("export_to_excel")}
            </Button>
          </Can>,
          <Can key={4} perform={Permissions.CAN_CREATE_TRIP}>
            <Divider key={3} type="vertical" />
            <Button
              type="primary"
              onClick={() => {
                navigate(`/trips/create`);
              }}
              icon={<PlusOutlined />}
            >
              {t("create_trip")}
            </Button>
          </Can>,
        ]}
      />
      <ExportToExcelModal
        id="export-trip"
        title={t("export_to_excel")}
        visible={exportModalVisible}
        toggleVisible={setExportModalVisible}
        width="50vw"
        defaultValues={
          {
            trip_id: false,
            trip_number: false,
            driver_id: false,
            driver_classification_id: false,
            calculating_delivery_cost_per_kilometer: false,
            delivery_cost_per_kilometer: false,
            extra_order_fixed_bonus_amount: false,
            extra_order_fixed_bonus_enabled: false,
            order_delivery_cost: false,
            store_id: false,
            status: false,
            orders_count: false,
            total_distance: false,
            base_cost: false,
            bonus_cost: false,
            total_cost: false,
            started_at: false,
            completed_at: false,
            cancelled_at: false,
            cancellation_reason: false,
            created_at: false,
          } as IExportTripExcel
        }
        loading={loading.includes("create")}
        onSubmit={async (columnsNames) => {
          await actions.exportExcel(columnsNames);
          setExportModalVisible(false);
        }}
      >
        <TemplateExportExcel names={columnsExportTripExcel} />
      </ExportToExcelModal>
      <SearchFilterCard
        formId="trip-filter"
        searchValue={search}
        setSearchValue={setSearch}
        onReset={() => {
          setSearch(undefined);
          actions.setQuery({
            page: query?.page,
            perPage: query?.perPage,
            filters: [],
          });
          const form = document.getElementById("trip-filter");
          if (form) {
            form.dispatchEvent(
              new Event("reset", { cancelable: true, bubbles: true })
            );
          }
        }}
        resetLoading={loading.includes("list")}
        applyLoading={loading.includes("list")}
      >
        <TripFilter />
      </SearchFilterCard>

      <TripTable />
    </div>
  );
};

const TripPage = () => {
  return (
    <TripContextProvider>
      <TripComponent />
    </TripContextProvider>
  );
};

export default TripPage;
